/**
 * Yahtzee Game Persistence Service
 *
 * Auto-saves game state to local storage after each action.
 * Follows LudoGamePersistence pattern.
 */

import {
  YahtzeeGameState,
  YahtzeeGameSettings,
  YahtzeeDifficulty,
} from '../types';
import { LocalStorageManager } from '../../services/LocalStorageManager';
import { DataSynchronizationService } from '../../services/DataSynchronizationService';

export interface SavedYahtzeeGame {
  gameId: string;
  mode: YahtzeeGameSettings['mode'];
  gameState: YahtzeeGameState;
  metadata: {
    aiDifficulty?: YahtzeeDifficulty;
    startedAt: number;
    lastSavedAt: number;
  };
}

export class YahtzeePersistence {
  private storage: LocalStorageManager;
  private syncService: DataSynchronizationService | null;
  private readonly STORAGE_KEY = 'yahtzee_saved_games';
  private readonly AUTO_SAVE_KEY = 'yahtzee_auto_save';

  constructor(
    storage: LocalStorageManager,
    syncService?: DataSynchronizationService
  ) {
    this.storage = storage;
    this.syncService = syncService || null;
  }

  /** Auto-save the current game state after each action. */
  autoSave(
    mode: YahtzeeGameSettings['mode'],
    gameState: YahtzeeGameState,
    metadata: SavedYahtzeeGame['metadata']
  ): boolean {
    try {
      const savedGame: SavedYahtzeeGame = {
        gameId: gameState.gameId,
        mode,
        gameState,
        metadata: { ...metadata, lastSavedAt: Date.now() },
      };
      localStorage.setItem(this.AUTO_SAVE_KEY, JSON.stringify(savedGame));
      return true;
    } catch (error) {
      console.error('Failed to auto-save yahtzee game:', error);
      return false;
    }
  }

  /** Load auto-saved game. */
  loadAutoSave(): SavedYahtzeeGame | null {
    try {
      const data = localStorage.getItem(this.AUTO_SAVE_KEY);
      if (!data) return null;
      return JSON.parse(data) as SavedYahtzeeGame;
    } catch (error) {
      console.error('Failed to load yahtzee auto-save:', error);
      return null;
    }
  }

  /** Clear auto-save data. */
  clearAutoSave(): void {
    try {
      localStorage.removeItem(this.AUTO_SAVE_KEY);
    } catch (error) {
      console.error('Failed to clear yahtzee auto-save:', error);
    }
  }

  /** Check if an auto-save exists. */
  hasAutoSave(): boolean {
    return localStorage.getItem(this.AUTO_SAVE_KEY) !== null;
  }

  /** Save a named game slot. */
  saveGame(name: string, mode: YahtzeeGameSettings['mode'], gameState: YahtzeeGameState, metadata: SavedYahtzeeGame['metadata']): boolean {
    try {
      const saves = this.getAllSaves();
      saves[name] = {
        gameId: gameState.gameId,
        mode,
        gameState,
        metadata: { ...metadata, lastSavedAt: Date.now() },
      };
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(saves));
      return true;
    } catch (error) {
      console.error('Failed to save yahtzee game:', error);
      return false;
    }
  }

  /** Load a named game slot. */
  loadGame(name: string): SavedYahtzeeGame | null {
    try {
      const saves = this.getAllSaves();
      return saves[name] || null;
    } catch (error) {
      console.error('Failed to load yahtzee game:', error);
      return null;
    }
  }

  /** Delete a named save. */
  deleteGame(name: string): boolean {
    try {
      const saves = this.getAllSaves();
      delete saves[name];
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(saves));
      return true;
    } catch (error) {
      console.error('Failed to delete yahtzee save:', error);
      return false;
    }
  }

  /** Get all saved games. */
  private getAllSaves(): Record<string, SavedYahtzeeGame> {
    try {
      const data = localStorage.getItem(this.STORAGE_KEY);
      return data ? JSON.parse(data) : {};
    } catch {
      return {};
    }
  }
}
